import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Package, 
  Edit, 
  Copy, 
  Trash2, 
  Plus, 
  Minus, 
  ArrowLeftRight, 
  ShoppingCart,
  FileText,
  AlertTriangle,
  MapPin,
  Tag,
  Calendar
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { productService } from '../../services/productService';
import { supplierService } from '../../services/supplierService';
import { cartService } from '../../services/cartService';
import { Product, StockMovement, SupplierPrice } from '../../types';

function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  
  const [product, setProduct] = useState<Product | null>(null);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [supplierPrices, setSupplierPrices] = useState<SupplierPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [adjustmentQuantity, setAdjustmentQuantity] = useState(0);
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [showAdjustment, setShowAdjustment] = useState(false);

  useEffect(() => {
    if (id) {
      loadProductData();
    }
  }, [id]);

  const loadProductData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const [productData, movementsData, pricesData] = await Promise.all([
        productService.getProduct(id),
        productService.getStockMovements(id),
        supplierService.getSupplierPrices(id)
      ]);
      
      setProduct(productData);
      setMovements(movementsData);
      setSupplierPrices(pricesData);
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur de chargement',
        message: 'Impossible de charger les détails du produit',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustStock = async () => {
    if (!product || !user || adjustmentQuantity === 0) return;
    
    try {
      await productService.adjustStock(
        product.id,
        adjustmentQuantity,
        adjustmentReason || 'Ajustement manuel',
        user.id
      );
      
      addNotification({
        userId: user.id,
        title: 'Stock ajusté',
        message: `Stock ajusté de ${adjustmentQuantity > 0 ? '+' : ''}${adjustmentQuantity}`,
        type: 'success'
      });
      
      setShowAdjustment(false);
      setAdjustmentQuantity(0);
      setAdjustmentReason('');
      loadProductData();
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur',
        message: 'Impossible d\'ajuster le stock',
        type: 'error'
      });
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    cartService.addToCart(product.id, 1);
    addNotification({
      userId: user?.id || '',
      title: 'Produit ajouté',
      message: 'Le produit a été ajouté au panier',
      type: 'success'
    });
  };

  const canManageProducts = user?.role === 'admin' || user?.role === 'responsable_depot' || user?.role === 'responsable_bureau';
  const canAdjustStock = canManageProducts;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-12">
        <Package className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Produit non trouvé</h3>
        <Link to="/products" className="mt-2 text-blue-600 hover:text-blue-800">
          Retour à la liste
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-gradient-to-br from-green-500 to-green-700 rounded-lg shadow-lg">
                <Package className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{product.designation}</h1>
                <p className="text-gray-600">Référence: {product.reference}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleAddToCart}
                className="btn-secondary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
              >
                <ShoppingCart className="h-4 w-4" />
                <span>Ajouter au panier</span>
              </button>
              
              {canManageProducts && (
                <>
                  <Link
                    to={`/products/${product.id}/edit`}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <Edit className="h-5 w-5" />
                  </Link>
                  
                  <button
                    onClick={() => setShowAdjustment(true)}
                    className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Ajuster le stock"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Product Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 sm:px-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informations produit</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Catégorie
                  </label>
                  <p className="text-sm text-gray-900">
                    {productService.getCategoryName(product.categoryId)}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Marque
                  </label>
                  <p className="text-sm text-gray-900">
                    {productService.getBrandName(product.brandId)}
                  </p>
                </div>
                
                {product.barcode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Code-barres
                    </label>
                    <p className="text-sm text-gray-900 font-mono">
                      {product.barcode}
                    </p>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Emplacement par défaut
                  </label>
                  <p className="text-sm text-gray-900">
                    {productService.getLocationName(product.defaultLocationId)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Stock Info */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 sm:px-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Stock</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {product.currentStock}
                  </div>
                  <div className="text-sm text-blue-600">Stock actuel</div>
                </div>
                
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">
                    {product.reservedStock}
                  </div>
                  <div className="text-sm text-orange-600">Stock réservé</div>
                </div>
                
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {productService.getAvailableStock(product)}
                  </div>
                  <div className="text-sm text-green-600">Stock disponible</div>
                </div>
              </div>
              
              <div className="mt-4 flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">
                  Seuil minimum: {product.minThreshold}
                </span>
                {productService.isStockAlert(product) && (
                  <div className="flex items-center text-orange-600">
                    <AlertTriangle className="h-4 w-4 mr-1" />
                    <span className="text-sm font-medium">Stock en alerte</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Movements */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 sm:px-8 border-b border-gray-200/50">
              <h3 className="text-lg font-medium text-gray-900">
                Mouvements récents ({movements.length})
              </h3>
            </div>
            
            {movements.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">Aucun mouvement récent</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {movements.slice(0, 5).map((movement) => (
                  <div key={movement.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${
                          movement.type === 'entry' ? 'bg-green-100 text-green-600' :
                          movement.type === 'exit' ? 'bg-red-100 text-red-600' :
                          movement.type === 'adjustment' ? 'bg-blue-100 text-blue-600' :
                          'bg-orange-100 text-orange-600'
                        }`}>
                          {movement.type === 'entry' ? <Plus className="h-4 w-4" /> :
                           movement.type === 'exit' ? <Minus className="h-4 w-4" /> :
                           movement.type === 'adjustment' ? <Edit className="h-4 w-4" /> :
                           <ArrowLeftRight className="h-4 w-4" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {movement.reason}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(movement.createdAt).toLocaleString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <div className={`text-sm font-medium ${
                        movement.quantity > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Product Image */}
          {product.image && (
            <div className="glass-card rounded-xl overflow-hidden">
              <img
                src={product.image}
                alt={product.designation}
                className="w-full h-64 object-cover"
              />
            </div>
          )}

          {/* Supplier Prices */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 border-b border-gray-200/50">
              <h3 className="text-lg font-medium text-gray-900">
                Fournisseurs & Prix
              </h3>
            </div>
            
            {supplierPrices.length === 0 ? (
              <div className="text-center py-8">
                <Tag className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">Aucun prix fournisseur</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {supplierPrices.map((price) => (
                  <div key={price.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {supplierService.getSupplierName(price.supplierId)}
                        </p>
                        <p className="text-xs text-gray-500">
                          Min: {price.minQuantity} unités
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {price.price.toFixed(2)} €
                        </p>
                        <p className="text-xs text-gray-500">HT/unité</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="glass-card rounded-xl">
            <div className="px-6 py-5 border-b border-gray-200/50">
              <h3 className="text-lg font-medium text-gray-900">Actions rapides</h3>
            </div>
            
            <div className="p-6 space-y-3">
              <button
                onClick={handleAddToCart}
                className="w-full btn-secondary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <ShoppingCart className="h-4 w-4" />
                <span>Ajouter au panier</span>
              </button>
              
              {canManageProducts && (
                <>
                  <Link
                    to={`/suppliers/orders/new?productId=${product.id}`}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                  >
                    <FileText className="h-4 w-4" />
                    <span>Créer commande fournisseur</span>
                  </Link>
                  
                  <button
                    onClick={() => setShowAdjustment(true)}
                    className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Ajuster le stock</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stock Adjustment Modal */}
      {showAdjustment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Ajuster le stock
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantité (+ pour ajouter, - pour retirer)
                  </label>
                  <input
                    type="number"
                    value={adjustmentQuantity}
                    onChange={(e) => setAdjustmentQuantity(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="0"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Raison
                  </label>
                  <input
                    type="text"
                    value={adjustmentReason}
                    onChange={(e) => setAdjustmentReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Raison de l'ajustement"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAdjustment(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleAdjustStock}
                  disabled={adjustmentQuantity === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
                >
                  Ajuster
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductDetail;